import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { type BasicSigningModuleSpecsFragment } from '../../../../api/fragments/BasicSigningModuleSpecs';
import {
    UpdateBasicSigningModuleDocument,
    type UpdateBasicSigningModuleMutation,
    type UpdateBasicSigningModuleMutationVariables,
} from '../../../../api/mutations/updateBasicSigningModule';
import { TransportProtocolType } from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import InputField from '../../../../components/fields/InputField';
import InputNumberField from '../../../../components/fields/InputNumberField';
import SelectField from '../../../../components/fields/SelectField';
import useHandleError from '../../../../utilities/useHandleError';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type UpdateFormProps = {
    module: BasicSigningModuleSpecsFragment;
    onComplete: () => void;
};

type FormValues = {
    displayName: string;
    lifeTime: number;
    transportProtocol: TransportProtocolType;
};

export const OTPFormValidator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.requiredNumber('lifeTime'),
    validators.requiredStringEnum('transportProtocol', TransportProtocolType)
);

const UpdateForm = ({ module, onComplete }: UpdateFormProps) => {
    const { t } = useTranslation('basicSigningModuleDetails');
    const { transportProtocolOptions } = useSystemOptions();

    const initialValues: FormValues = {
        displayName: module.displayName,
        lifeTime: module.lifeTime,
        transportProtocol: module.transportProtocol,
    };

    const apolloClient = useApolloClient();
    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('basicSigningModuleDetails:updateDrawer.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateBasicSigningModuleMutation, UpdateBasicSigningModuleMutationVariables>({
                    mutation: UpdateBasicSigningModuleDocument,
                    variables: { moduleId: module.id, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('basicSigningModuleDetails:updateDrawer.messages.success'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [onComplete, t, apolloClient, module]
    );

    const validate = useValidator(OTPFormValidator);

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form id="updateForm" name="updateForm" onSubmitCapture={handleSubmit}>
                    <InputField
                        {...t('basicSigningModuleDetails:updateDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                    <InputNumberField
                        {...t('basicSigningModuleDetails:updateDrawer.fields.lifeTime', {
                            returnObjects: true,
                        })}
                        max={10}
                        min={1}
                        name="lifeTime"
                        required
                    />
                    <SelectField
                        {...t('basicSigningModuleDetails:updateDrawer.fields.transportProtocol', {
                            returnObjects: true,
                        })}
                        name="transportProtocol"
                        options={transportProtocolOptions}
                        required
                        showSearch
                    />
                </Form>
            )}
        </Formik>
    );
};

export default UpdateForm;
